import { BehaviorSubject, Observable, Observer, Subject } from "rxjs";
import { io, Socket as _Socket } from "socket.io-client";
import { ErrorData } from "./domain/interface";

/**
 * Room socket (youm-api) with its own recovery.
 *
 * socket.io retries by itself only after a transport loss. It never retries
 * after "io server disconnect" or a connect error raised by a server
 * middleware, so a socket closed that way stayed closed while the page kept
 * running (session 839721, 2026-11-03: a guest stayed off the room for 18 min).
 * Every non-final loss is retried here, socket.io's own reconnection is off:
 * - fast phase: RECONNECT_ATTEMPTS retries with an exponential, randomized
 *   backoff (1 s -> 15 s), state 'reconnecting';
 * - then slow phase: state 'failed' (the user is told), still one retry every
 *   SLOW_RETRY_MS, at once when the browser is back online or the tab visible
 *   again (unattended capture and viewer pages recover by themselves);
 * - final: an explicit disconnect() and an authentication refusal (1401/1403).
 * Failures count until a connection stays up STABLE_MS, so a socket that
 * connects then drops at once keeps backing off.
 */
export class Socket {
  static readonly RECONNECT_ATTEMPTS = 20;
  static readonly RECONNECT_DELAY_MS = 1_000;
  static readonly RECONNECT_DELAY_MAX_MS = 15_000;
  // Spread the retries of many clients after a server restart
  static readonly RECONNECT_RANDOMIZATION = 0.5;
  // First retry spread over [RECONNECT_DELAY_MS / 2, this]: after a server
  // restart, the rooms' clients do not all come back in the same second
  static readonly FIRST_RETRY_SPREAD_MS = 5_000;
  static readonly SLOW_RETRY_MS = 60_000;
  static readonly STABLE_MS = 30_000;
  static readonly AUTH_ERROR_CODES: readonly number[] = [1401, 1403];

  private _socket: null | _Socket = null;
  private readonly _connected$: BehaviorSubject<boolean>;
  private readonly _disconnect$: Subject<string>;
  private readonly _connectError$: Subject<SocketConnectError>;
  private readonly _responseError$ = new Subject<ErrorResponse>();
  private readonly _state$ = new BehaviorSubject<SocketStateVO>({
    state: "idle"
  });
  private readonly _lifecycle$ = new Subject<SocketLifecycleEvent>();

  // Recovery of the current socket
  private _everConnected = false;
  // The current attempt reached 'connect' (vs. dropped during its handshake)
  private _isConnected = false;
  // Retries of the current outage (logs)
  private _attempt = 0;
  // Failed recoveries since the last connection that stayed up (backoff)
  private _failures = 0;
  private _disconnectedAt: null | number = null;
  // socket.io clears socket.id on disconnect: kept for the logs
  private _lastSocketId: null | string = null;
  private _retryTimer: null | ReturnType<typeof setTimeout> = null;
  private _stableTimer: null | ReturnType<typeof setTimeout> = null;
  private _final = false;
  private _wakeOff: Array<() => void> = [];

  constructor(private readonly _config: Config) {
    this._connected$ = new BehaviorSubject<boolean>(false);
    this._disconnect$ = new Subject<string>();
    this._connectError$ = new Subject<SocketConnectError>();
  }

  get id(): null | string {
    return this._socket?.id || null;
  }

  get connected$(): Observable<boolean> {
    return this._connected$.asObservable();
  }

  get disconnect$(): Observable<string> {
    return this._disconnect$.asObservable();
  }

  get connectError$(): Observable<SocketConnectError> {
    return this._connectError$.asObservable();
  }

  get responseError$(): Observable<ErrorResponse> {
    return this._responseError$.asObservable();
  }

  /**
   * Connection state, for the user: 'reconnecting' while a recovery is in its
   * fast phase, 'failed' once it is slow (still retrying) or final
   * (authentication refused), 'closed' after disconnect(cause).
   */
  get state$(): Observable<SocketStateVO> {
    return this._state$.asObservable();
  }

  get state(): SocketStateVO {
    return this._state$.value;
  }

  /** Every disconnect, retry and result, for the logs. */
  get lifecycle$(): Observable<SocketLifecycleEvent> {
    return this._lifecycle$.asObservable();
  }

  emit<R, D>(event: string, data: D): Observable<R> {
    const timeoutMs = 15_000;

    return new Observable((observer: Observer<R>) => {
      const socket = this._socket;
      // Not connected (outage, handshake in progress): refused at once rather
      // than buffered by socket.io, which would send it before the room is
      // reset and joined again
      if (!socket?.connected) {
        observer.error({
          code: 1408,
          message: `Socket not connected for ${event}`
        });
        return;
      }

      const timeout = setTimeout(() => {
        observer.error({
          code: 1408,
          message: `Emit response timeout for ${event}: ${JSON.stringify(data)}`
        });
      }, timeoutMs);

      socket.emit(event, data, (_: ErrorResponse | Response<R>) => {
        clearTimeout(timeout);

        if (_ && (_ as ErrorResponse).code !== 0) {
          const err = _ as ErrorResponse;
          this._responseError$.next(err);
          return observer.error(err);
        }

        observer.next((_ as Response<R>).payload);
        observer.complete();
      });

      return () => {
        clearTimeout(timeout);
        observer.complete();
      };
    });
  }

  connect(data: SocketConnectData) {
    // One socket at a time: a previous one would keep reconnecting
    this.close();

    const socket = io(`${this._config.url}`, {
      closeOnBeforeunload: data.closeOnBeforeunload,
      // Own Manager: its state belongs to this socket only
      forceNew: true,
      // Don't support polling
      // server does not allow CORS for HTTP connection
      transports: ["websocket"],
      // Don't use headers
      // Custom headers will not be appended when using websocket as the transport.
      query: data.token ? { token: data.token } : {},
      // Retried here, for every cause (see the class comment)
      reconnection: false,
      timeout: 15_000
    });
    this._socket = socket;
    this._everConnected = false;
    this._attempt = 0;
    this._failures = 0;
    this._final = false;
    this._disconnectedAt = null;
    this._lastSocketId = null;
    this.setState({ state: "connecting" });

    socket.on("connect", () => this.onConnect(socket));
    socket.on("connect_error", (error: SocketConnectError) =>
      this.onConnectError(socket, error)
    );
    socket.on("disconnect", (reason: string) =>
      this.onDisconnect(socket, reason)
    );
    this.listenWake(socket);
  }

  /**
   * Explicit close (leave, sign out, token change): never reconnected.
   * With a cause, the state becomes 'closed' with that cause, so the user can
   * be told the room connection is gone.
   */
  disconnect(cause?: string) {
    const socketId = this.id ?? this._lastSocketId;
    const wasOpen = !!this._socket;
    // Already ended by an authentication refusal (logged as such)
    const authRefused =
      this._state$.value.state === "failed" &&
      this._state$.value.code !== undefined;

    this.close();
    this._connected$.next(false);

    if (cause) {
      this.setState({ state: "closed", reason: cause });
      if (wasOpen && !authRefused) {
        this._lifecycle$.next({
          type: "disconnect",
          reason: cause,
          expected: true,
          socketId
        });
      }
    } else {
      this.setState({ state: "idle" });
    }
  }

  /** Retries now a recovery that waits (no effect otherwise). */
  reconnectNow() {
    const socket = this._socket;
    if (!socket || this._final || this._retryTimer === null) {
      return;
    }
    this.clearRetry();
    this.retry(socket, "wake");
  }

  listen<T>(action: string): Observable<T> {
    return new Observable((observer: Observer<T>) => {
      function callback(message: T): void {
        observer.next(message);
      }

      this._socket?.on(action, callback);

      return () => {
        this._socket?.off(action, callback);
      };
    });
  }

  private close() {
    this.clearRetry();
    this.clearStable();
    this._isConnected = false;
    this._wakeOff.forEach((off) => off());
    this._wakeOff = [];

    const socket = this._socket;
    this._socket = null;
    if (!socket) {
      return;
    }

    socket.removeAllListeners();
    socket.disconnect();
  }

  private onConnect(socket: _Socket) {
    if (this._socket !== socket) return;

    const recovered = this._disconnectedAt !== null;
    const downtimeMs = recovered ? Date.now() - this._disconnectedAt! : 0;
    const attempts = this._attempt;

    this._everConnected = true;
    this._isConnected = true;
    this._lastSocketId = socket.id ?? null;
    this.clearRetry();
    this._attempt = 0;
    this._disconnectedAt = null;
    // The backoff restarts only once the connection held
    this.clearStable();
    this._stableTimer = setTimeout(() => {
      this._stableTimer = null;
      this._failures = 0;
    }, Socket.STABLE_MS);
    this.setState({ state: "connected" });
    this._connected$.next(true);

    if (recovered) {
      this._lifecycle$.next({
        type: "reconnect",
        attempt: attempts,
        downtimeMs,
        socketId: socket.id
      });
    } else {
      this._lifecycle$.next({ type: "connect", socketId: socket.id });
    }
  }

  private onDisconnect(socket: _Socket, reason: string) {
    if (this._socket !== socket) return;

    const wasConnected = this._isConnected;
    this._isConnected = false;
    this.clearStable();

    if (wasConnected) {
      this._disconnectedAt = Date.now();
      this._disconnect$.next(reason);
      this._connected$.next(false);

      // Not through close() (whose listeners are removed first): unexpected,
      // whatever the reason ('transport close', 'ping timeout', 'io server
      // disconnect', 'io client disconnect' from elsewhere...)
      this._lifecycle$.next({
        type: "disconnect",
        reason,
        expected: false,
        willRetry: true,
        socketId: this._lastSocketId
      });
    } else {
      // A retry dropped before its handshake was accepted: same outage
      this._lifecycle$.next({
        type: "connect_error",
        message: reason,
        willRetry: !this._final
      });
    }

    // One retry per failed attempt (its connect_error may already have
    // scheduled it)
    if (this._retryTimer === null) {
      this.scheduleRetry(socket, reason);
    }
  }

  private onConnectError(socket: _Socket, error: SocketConnectError) {
    if (this._socket !== socket) return;

    const code = error.data?.code;
    // An authentication refusal: retrying the same token can't succeed
    if (code !== undefined && Socket.AUTH_ERROR_CODES.includes(code)) {
      this._lifecycle$.next({
        type: "connect_error",
        code,
        message: error.message,
        willRetry: false
      });
      // Before the error is published: its handlers may close the socket
      this.fail("authentication refused", code);
      this._connectError$.next(error);
      return;
    }

    this._lifecycle$.next({
      type: "connect_error",
      code,
      message: error.message,
      willRetry: true
    });
    if (this._disconnectedAt === null && this._everConnected) {
      this._disconnectedAt = Date.now();
    }
    if (this._retryTimer === null) {
      this.scheduleRetry(socket, error.message);
    }
    this._connectError$.next(error);
  }

  private scheduleRetry(socket: _Socket, reason: string) {
    this.clearRetry();
    if (this._final) return;

    this._failures++;
    const attempt = ++this._attempt;
    const slow = this._failures > Socket.RECONNECT_ATTEMPTS;

    if (slow) {
      if (this._state$.value.state !== "failed") {
        this.setState({ state: "failed", reason: "unreachable", attempt });
        this._lifecycle$.next({
          type: "reconnect_failed",
          reason: `${reason}: fast retries exhausted, retrying every ${Socket.SLOW_RETRY_MS} ms`,
          attempt: attempt - 1
        });
      }
    } else {
      this.setState({
        state: this._everConnected ? "reconnecting" : "connecting",
        attempt
      });
    }

    this._retryTimer = setTimeout(
      () => {
        this._retryTimer = null;
        if (this._socket !== socket) return;
        this.retry(socket, "timer");
      },
      slow ? Socket.slowRetryDelayMs() : Socket.retryDelayMs(this._failures)
    );
  }

  private retry(socket: _Socket, by: "timer" | "wake") {
    this._lifecycle$.next({
      type: "reconnect_attempt",
      attempt: this._attempt,
      by: by === "wake" ? "wake" : "youm"
    });
    socket.connect();
  }

  // Final until a new connect()
  private fail(reason: string, code?: number) {
    this.clearRetry();
    this.clearStable();
    this._isConnected = false;
    this._final = true;
    this._connected$.next(false);
    this.setState({ state: "failed", reason, code });
    this._lifecycle$.next({
      type: "reconnect_failed",
      reason,
      code,
      attempt: this._attempt
    });
  }

  // Back online, or the tab visible again: no need to wait for the timer
  private listenWake(socket: _Socket) {
    const target = globalThis as unknown as {
      addEventListener?: (type: string, listener: () => void) => void;
      removeEventListener?: (type: string, listener: () => void) => void;
      document?: { visibilityState?: string };
    };
    if (typeof target.addEventListener !== "function") {
      return;
    }
    const onOnline = () => this._socket === socket && this.reconnectNow();
    const onVisible = () =>
      target.document?.visibilityState === "visible" &&
      this._socket === socket &&
      this.reconnectNow();
    target.addEventListener("online", onOnline);
    target.addEventListener("visibilitychange", onVisible);
    this._wakeOff = [
      () => target.removeEventListener?.("online", onOnline),
      () => target.removeEventListener?.("visibilitychange", onVisible)
    ];
  }

  private clearRetry() {
    if (this._retryTimer !== null) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
  }

  private clearStable() {
    if (this._stableTimer !== null) {
      clearTimeout(this._stableTimer);
      this._stableTimer = null;
    }
  }

  private setState(state: SocketStateVO) {
    const current = this._state$.value;
    if (
      current.state === state.state &&
      current.attempt === state.attempt &&
      current.reason === state.reason &&
      current.code === state.code
    ) {
      return;
    }
    this._state$.next(state);
  }

  // In [0, 1): spreads the retries of many clients (not a secret; same
  // generator as the other retry jitters of the front)
  static random(): number {
    const [value = 0] = crypto.getRandomValues(new Uint32Array(1));
    return value / 2 ** 32;
  }

  // Same backoff shape as socket.io: exponential, randomized, capped; the
  // first retry is spread wider (many clients lose the server at once)
  static retryDelayMs(attempt: number, random = Socket.random()): number {
    if (attempt <= 1) {
      const min = Socket.RECONNECT_DELAY_MS / 2;
      const max = Math.max(min, Socket.FIRST_RETRY_SPREAD_MS);
      return Math.round(min + (max - min) * random);
    }
    const base = Math.min(
      Socket.RECONNECT_DELAY_MS * 2 ** Math.max(0, attempt - 1),
      Socket.RECONNECT_DELAY_MAX_MS
    );
    const deviation = base * Socket.RECONNECT_RANDOMIZATION;
    return Math.min(
      Math.round(base - deviation + 2 * deviation * random),
      Socket.RECONNECT_DELAY_MAX_MS
    );
  }

  static slowRetryDelayMs(random = Socket.random()): number {
    const deviation = Socket.SLOW_RETRY_MS * Socket.RECONNECT_RANDOMIZATION;
    return Math.round(
      Socket.SLOW_RETRY_MS - deviation + 2 * deviation * random
    );
  }
}

export type SocketState =
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "failed"
  | "closed";

export interface SocketStateVO {
  state: SocketState;
  attempt?: number;
  reason?: string;
  code?: number;
}

export type SocketLifecycleEvent =
  | { type: "connect"; socketId?: null | string }
  | {
      type: "disconnect";
      reason: string;
      expected: boolean;
      willRetry?: boolean;
      socketId?: null | string;
    }
  | { type: "reconnect_attempt"; attempt: number; by: "youm" | "wake" }
  | {
      type: "reconnect";
      attempt: number;
      downtimeMs: number;
      socketId?: null | string;
    }
  | {
      type: "connect_error";
      code?: number;
      message: string;
      willRetry: boolean;
    }
  | {
      type: "reconnect_failed";
      reason: string;
      code?: number;
      attempt: number;
    };

export type SocketConnectData = Partial<{
  token: string;
  closeOnBeforeunload: boolean;
}>;

// https://socket.io/docs/v3/middlewares/#handling-middleware-error
export interface SocketConnectError extends Error {
  data?: ErrorData;
}

interface Response<T> {
  code: number;
  payload: T;
}

export interface ErrorResponse {
  code: number;
  message: string;
  errorId?: string;
}

interface Config {
  url: string;
}
